<script setup>
import { useI18n } from 'vue-i18n';
import Trans from '@/i18n/translation';
import GamebookLogo from '@/components/GamebookLogo.vue';
import MailIcon from '@/components/CustomIcons/MailIcon.vue';
import LinkedInIcon from '@/components/CustomIcons/LinkedInIcon.vue';

const i18n = useI18n();

const navigation = {
    product: [
        {
            name: i18n.t('nav.howItWorks'),
            href: Trans.i18nRoute({ name: 'home', hash: '#how-it-works' }),
        },
        { name: i18n.t('nav.pricing'), href: Trans.i18nRoute({ name: 'home', hash: '#pricing' }) },
    ],
    company: [
        {
            name: i18n.t('nav.whyCyberalarm'),
            href: Trans.i18nRoute({ name: 'home', hash: '#why-cyberalarm' }),
        },
        { name: i18n.t('nav.aboutUs'), href: Trans.i18nRoute({ name: 'home', hash: '#about-us' }) },
        { name: i18n.t('nav.contact'), href: Trans.i18nRoute({ name: 'contact' }) },
    ],
    legal: [
        {
            name: i18n.t('nav.imprint'),
            href: Trans.i18nRoute({ name: 'imprint' }),
        },
        { name: i18n.t('nav.privacy'), href: 'privacy-policy' },
        { name: i18n.t('nav.terms'), href: 'terms-and-conditions' },
    ],
};
</script>

<template>
    <footer class="text-white text-sm relative z-10 " aria-labelledby="footer-heading">
        <h2 id="footer-heading" class="sr-only">Footer</h2>
        <div class="mx-auto max-w-7xl px-6 pb-8 pt-16 sm:pt-24 lg:px-8">
            <div class="xl:grid xl:grid-cols-2 xl:gap-8">
                <div class="space-y-8">
                    <span class="flex sm:justify-start justify-center items-center gap-2">
                        <GamebookLogo />
                        <p class="text-lg font-bold">
                            {{ $t('footer.title') }}
                        </p>
                    </span>
                    <span class="flex sm:justify-start justify-center gap-2">
                        <p class="text-lg text-gray-400">
                            {{ $t('footer.slogan') }}
                        </p>
                    </span>
                    <span class="flex gap-2">
                        <p class="text-xs text-gray-400">
                            {{ $t('company.address1') }}<br>
                            {{ $t('company.address2') }}<br>
                            {{ $t('company.address3') }}<br>
                            {{ $t('company.address4') }}<br>
                        </p>
                    </span>
                    <div class="text-left flex flex-col gap-4">
                        <span class="flex gap-2 items-center text-gray-400 hover:text-white transition-colors">
                            <MailIcon class="w-5 h-5" />
                            <p class="text-xs">
                                {{ $t('company.email') }}
                            </p>
                        </span>
                        <span class="flex gap-2">
                            <a href="https://www.linkedin.com/company/cyberalarmtraining" target="_blank"
                                rel="noopener noreferrer"
                                class="flex items-center px-1 gap-2 text-xs text-gray-400 hover:text-terciary transition-colors">
                                <LinkedInIcon class="w-5 h-5" />
                                <span>{{ $t('company.linkedin') }}</span>
                            </a>
                        </span>
                    </div>

                </div>
                <div class="mt-16 sm:grid sm:grid-cols-3 flex flex-col gap-8 xl:mt-0">
                    <div>
                        <h3
                            class="text-sm font-semibold leading-6 text-white mb-4 border-b border-terciary/30 pb-2 inline-block">
                            {{ $t('footer.product') }}
                        </h3>
                        <ul role="list" class="mt-4 space-y-3">
                            <li v-for="item in navigation.product" :key="item.name">
                                <RouterLink :to="item.href"
                                    class="text-sm leading-6 text-gray-400 hover:text-terciary transition-colors">
                                    {{ item.name }}
                                </RouterLink>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <h3
                            class="text-sm font-semibold leading-6 text-white mb-4 border-b border-terciary/30 pb-2 inline-block">
                            {{ $t('footer.company') }}
                        </h3>
                        <ul role="list" class="mt-4 space-y-3">
                            <li v-for="item in navigation.company" :key="item.name">
                                <RouterLink :to="item.href"
                                    class="text-sm leading-6 text-gray-400 hover:text-terciary transition-colors">
                                    {{ item.name }}
                                </RouterLink>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <h3
                            class="text-sm font-semibold leading-6 text-white mb-4 border-b border-terciary/30 pb-2 inline-block">
                            {{ $t('footer.legal') }}
                        </h3>
                        <ul role="list" class="mt-4 space-y-3">
                            <li v-for="item in navigation.legal" :key="item.name">
                                <RouterLink :to="item.href"
                                    class="text-sm leading-6 text-gray-400 hover:text-terciary transition-colors">
                                    {{ item.name }}
                                </RouterLink>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div
                class="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-6">
                <p class="text-xs leading-5 text-gray-500">
                    &copy; {{ $t('company.copyright') }}
                </p>
            </div>
        </div>
    </footer>
</template>
